import type { ChildGuideActions, ChildGuideItem } from '@tunein/web-api-client';
import type { ComponentType } from 'react';
import css from './guide-item-actions.module.scss';
import { RemoveRecentAction } from './RemoveRecentAction';

export interface GuideItemActionProps {
  guideItem: ChildGuideItem;
}

// Item-level actions the api-client puts on `guideItem.actions` (see ChildGuideActions /
// CHILD_ACTION_KEYS). Add an entry here to support a new one.
const actionComponents: Partial<
  Record<keyof ChildGuideActions, ComponentType<GuideItemActionProps>>
> = {
  removeRecent: RemoveRecentAction,
};

interface GuideItemActionsProps {
  guideItem?: ChildGuideItem;
}

/**
 * Row-level actions revealed when a guide item is hovered or focused. Which actions appear is
 * decided entirely by what the API puts on the item, so there's no client-side gate to keep in
 * sync. Deliberately hook-free: it renders for every row in every list, and bails out before any
 * action component (and its context/state work) mounts.
 */
export function GuideItemActions({ guideItem }: GuideItemActionsProps) {
  const availableActions = (Object.keys(actionComponents) as Array<keyof ChildGuideActions>).filter(
    (name) => guideItem?.actions?.[name],
  );

  if (!guideItem || !availableActions.length) {
    return null;
  }

  return (
    <div className={css.actionsRow} data-guide-item-actions data-testid="guideItemActions">
      {availableActions.map((name) => {
        const Action = actionComponents[name] as ComponentType<GuideItemActionProps>;

        return <Action key={name} guideItem={guideItem} />;
      })}
    </div>
  );
}
